import api, {
    ResponseAndErrorCallback,
    ResponseCallback
} from '../services/api.service'
import routeUtil from '../utils/route.util'

export type LabelApprovalStatus =
    | 'pending'
    | 'approved'
    | 'rejected'
    | 'cancelled'
    | 'expired'

/**
 * A gallery row is either a custom label (`customLabels.id`) or one of the
 * global/contact/item/category label designs (`labels.id`), so an approval is
 * always addressed by the pair.
 */
export type LabelApprovalLabelableType = 'customLabel' | 'systemLabel'

export const APPROVAL_TEXT_MAX_LENGTH = 250

export interface LabelApprovalData {
    id: number
    status: LabelApprovalStatus
    labelWidth?: string | number | null
    labelHeight?: string | number | null
    labelUnit?: string | null
    isStale: boolean
    approverName: string
    approverEmail: string
    message?: string | null
    responderRemarks?: string | null
    snapshotUrl?: string | null
    requestedAt: string
    respondedAt?: string | null
    expiresAt?: string | null
    /** How many times the approver has been emailed about this request. */
    sentCount?: number
    lastSentAt?: string | null
}

/**
 * What the approver's public page receives — no internal ids.
 */
export interface LabelApprovalPublicData {
    labelName: string
    labelWidth?: string | number | null
    labelHeight?: string | number | null
    labelUnit?: string | null
    approverName: string
    message?: string | null
    snapshotUrl?: string | null
    status: LabelApprovalStatus
    isAnswerable: boolean
    requestedBy?: string | null
    requestedAt: string
    respondedAt?: string | null
    responderRemarks?: string | null
    remarksMaxLength: number
}

export interface LabelApprovalRequestPayload {
    labelableType: LabelApprovalLabelableType
    labelableId: number
    approverName: string
    approverEmail: string
    message?: string
}

export interface LabelApprovalRespondPayload {
    approved: boolean
    remarks?: string
}

/** CSS px per design unit — 1in is 96px by CSS definition. */
const pxPerUnit: Record<string, number> = {
    in: 96,
    cm: 37.7952755906,
    mm: 3.77952755906,
    pt: 1.3333333333,
    px: 1
}

const asNumber = (value?: string | number | null) => {
    const parsed = typeof value === 'string' ? parseFloat(value) : value
    return parsed && !isNaN(parsed) ? parsed : null
}

/**
 * Size to render the label at, mirroring the backend so the approver page and
 * the email show the same shape and a size proportional to the real label.
 * Returns null when the design declares no dimensions.
 */
export function labelDisplaySize (
    width?: string | number | null,
    height?: string | number | null,
    unit?: string | null,
    maxWidth = 460,
    maxHeight = 420
) {
    const w = asNumber(width)
    const h = asNumber(height)
    if (!w || !h) return null
    const factor = pxPerUnit[(unit ?? 'in').toLowerCase()] ?? 96
    const pxWidth = w * factor
    const pxHeight = h * factor
    const scale = Math.min(1, maxWidth / pxWidth, maxHeight / pxHeight)
    return {
        width: Math.round(pxWidth * scale),
        height: Math.round(pxHeight * scale)
    }
}

/** "6 x 4 in" — trims trailing zeros from the stored decimals. */
export function labelSizeText (
    width?: string | number | null,
    height?: string | number | null,
    unit?: string | null
) {
    const w = asNumber(width)
    const h = asNumber(height)
    if (!w || !h) return ''
    const trim = (n: number) => String(parseFloat(n.toFixed(3)))
    return `${trim(w)} x ${trim(h)} ${unit ?? ''}`.trim()
}

const routes = {
    request: '/labelApprovals',
    history: '/labelApprovals/{labelableType}/{labelableId}',
    cancel: '/labelApprovals/{id}/cancel',
    resend: '/labelApprovals/{id}/resend',
    show: '/labelApprovals/{token}',
    respond: '/labelApprovals/{token}/respond'
}

export default {
    request (
        payload: LabelApprovalRequestPayload,
        callback: ResponseCallback<LabelApprovalData>
    ) {
        api.post(routes.request, payload, callback)
    },

    history (
        labelableType: LabelApprovalLabelableType,
        labelableId: number,
        callback: ResponseCallback<LabelApprovalData[]>
    ) {
        const url = routeUtil.replaceParams(routes.history, {
            labelableType,
            labelableId
        })
        api.get(url, {}, callback)
    },

    cancel (id: number, callback: ResponseCallback<LabelApprovalData>) {
        const url = routeUtil.replaceParams(routes.cancel, { id })
        api.post(url, {}, callback)
    },

    /**
     * Email an existing pending request again, for a lost or spam-filed
     * message. Keeps the same record; issues a fresh link.
     */
    resend (id: number, callback: ResponseCallback<LabelApprovalData>) {
        const url = routeUtil.replaceParams(routes.resend, { id })
        api.post(url, {}, callback)
    },

    /**
     * Public: read an approval request by its emailed token. Errors are handed
     * back so the approver page can show why the link doesn't work rather than
     * a generic toast.
     */
    show (
        token: string,
        callback: ResponseCallback<LabelApprovalPublicData>,
        errorCallback: ResponseAndErrorCallback
    ) {
        const url = routeUtil.replaceParams(routes.show, { token })
        api.getAndError(url, {}, callback, errorCallback)
    },

    /**
     * Public: record the approver's decision. Single use.
     */
    respond (
        token: string,
        payload: LabelApprovalRespondPayload,
        callback: ResponseCallback<LabelApprovalPublicData>,
        errorCallback: ResponseAndErrorCallback
    ) {
        const url = routeUtil.replaceParams(routes.respond, { token })
        api.postAndError(url, payload, callback, errorCallback)
    }
}
